import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { LOGO_URL, scrollToId } from "../lib/api";

const links = [
  { label: "Services", href: "#services" },
  { label: "Manifesto", href: "#manifesto" },
  { label: "Results", href: "#results" },
  { label: "GMB", href: "#gmb" },
  { label: "Voices", href: "#testimonials" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const ids = links.map((l) => l.href.slice(1));
    const sections = ids
      .map((id) => document.getElementById(id))
      .filter(Boolean);
    if (!sections.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(`#${e.target.id}`);
        });
      },
      { rootMargin: "-45% 0px -50% 0px", threshold: 0 }
    );
    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, []);

  const go = (href) => {
    setOpen(false);
    scrollToId(href);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className="fixed inset-x-0 top-0 z-50"
    >
      <div
        className={`mx-auto flex max-w-7xl items-center justify-between px-5 py-3 transition-[background,box-shadow,margin] duration-500 md:px-8 ${
          scrolled
            ? "mt-3 rounded-full border border-slate-200 bg-white/70 shadow-sm backdrop-blur-xl md:mx-8"
            : "mt-0 border border-transparent"
        }`}
      >
        <a href="#top" data-testid="nav-logo" onClick={(e) => { e.preventDefault(); go("#top"); }} className="flex items-center gap-2">
          <img src={LOGO_URL} alt="Content Lab" className="h-9 w-auto object-contain" />
        </a>

        <nav className="hidden items-center gap-8 md:flex">
          {links.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-${l.label.toLowerCase()}`}
              onClick={() => go(l.href)}
              className={`relative font-mono text-xs uppercase tracking-[0.15em] transition-colors duration-300 hover:text-blue-deep ${
                active === l.href ? "text-blue-deep" : "text-slate-600"
              }`}
            >
              {l.label}
              {active === l.href && (
                <motion.span
                  layoutId="nav-active"
                  className="absolute -bottom-1.5 left-0 h-0.5 w-full rounded-full bg-lab-green"
                />
              )}
            </button>
          ))}
        </nav>

        <div className="hidden md:block">
          <button
            data-testid="nav-cta"
            onClick={() => go("#audit")}
            className="rounded-full bg-obsidian px-5 py-2.5 font-mono text-xs uppercase tracking-[0.15em] text-white transition-transform duration-300 hover:-translate-y-0.5"
          >
            Get in Touch
          </button>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="text-obsidian md:hidden"
          onClick={() => setOpen((o) => !o)}
          aria-label="Menu"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="mx-4 mt-2 overflow-hidden rounded-3xl border border-slate-200 bg-white p-4 shadow-lg md:hidden"
          >
            {links.map((l) => (
              <button
                key={l.href}
                data-testid={`nav-mobile-${l.label.toLowerCase()}`}
                onClick={() => go(l.href)}
                className="block w-full py-3 text-left font-display text-lg text-obsidian"
              >
                {l.label}
              </button>
            ))}
            <button
              data-testid="nav-mobile-cta"
              onClick={() => go("#audit")}
              className="mt-2 w-full rounded-full bg-obsidian py-3 font-mono text-xs uppercase tracking-[0.15em] text-white"
            >
              Get in Touch
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
